import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import { motion } from 'framer-motion';
import { Heart, Send, CheckCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { notifyAdminsAboutIntake } from '@/lib/adminIntakeNotifications';

export default function PedidoOracao() {
  const [formData, setFormData] = useState({
    name: '',
    request: '',
    is_anonymous: false
  });
  const [submitted, setSubmitted] = useState(false);

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const request = await base44.entities.PrayerRequest.create(data);
      await notifyAdminsAboutIntake({ type: 'prayer_request', recordId: request.id }).catch((error) => {
        console.error('Erro ao notificar administradores sobre pedido de oração:', error);
      });
      return request;
    },
    onSuccess: () => {
      setSubmitted(true);
      setFormData({ name: '', request: '', is_anonymous: false });
    },
    onError: () => {
      toast.error('Erro ao enviar pedido. Tente novamente.');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.request.trim()) {
      toast.error('Por favor, escreva seu pedido de oração.');
      return;
    }
    createMutation.mutate({
      ...formData,
      name: formData.is_anonymous ? '' : formData.name,
      status: 'Novo'
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-br from-rose-500 to-pink-600 text-white pt-8 pb-20 px-4">
          <div className="max-w-lg mx-auto">
            <PageHeader title="Pedido de Oração" showBack={true} />
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 -mt-14">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-lg p-8 text-center"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Pedido Enviado!</h2>
            <p className="text-gray-600 mb-6">
              Seu pedido de oração foi recebido. Nossa equipe estará orando por você.
            </p>
            <Button onClick={() => setSubmitted(false)} className="w-full">
              Enviar Novo Pedido
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 text-white pt-8 pb-20 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader title="Pedido de Oração" subtitle="Compartilhe seu pedido conosco" showBack={true} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 -mt-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
              <Heart className="w-6 h-6 text-rose-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Pedido de Oração</h2>
              <p className="text-sm text-gray-500">Estamos aqui para orar por você</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seu Nome (opcional)
              </label>
              <Input
                placeholder="Como podemos te chamar?"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={formData.is_anonymous}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seu Pedido de Oração *
              </label>
              <Textarea
                placeholder="Compartilhe seu pedido conosco..."
                value={formData.request}
                onChange={(e) => setFormData({ ...formData, request: e.target.value })}
                rows={5}
                className="resize-none"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="anonymous"
                checked={formData.is_anonymous}
                onCheckedChange={(checked) => setFormData({ 
                  ...formData, 
                  is_anonymous: checked,
                  name: checked ? '' : formData.name 
                })}
              />
              <label 
                htmlFor="anonymous" 
                className="text-sm text-gray-600 cursor-pointer"
              >
                Desejo que meu pedido seja anônimo
              </label>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-rose-500 hover:bg-rose-600"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? (
                'Enviando...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Pedido
                </>
              )}
            </Button>
          </form>

          <p className="text-xs text-gray-400 text-center mt-4">
            Seu pedido será tratado com total sigilo e respeito.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
